import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tooltip, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Power, Pencil, PowerOff } from "lucide-react";

import Modal from '../../../components/modal/Index';
import SnackBar from '../../../components/snackbar/snackbar';
import Page from '../../../components/Page';
import adminServices from '../../../services/adminServices';
import AddContentWriterForm from './AddContentWriterForm';
import ResponsiveTable from '../../../components/Table/Responsivetable';

export default function ContentCreatorList() {
  const { t } = useTranslation('user');
  const navigate = useNavigate();

  const [clientList, setClientList]       = useState([]);
  const [totalCount, setTotalCount]       = useState(0);
  const [page, setPage]                   = useState(0);
  const [rowsPerPage, setRowsPerPage]     = useState(10);
  const [search, setSearch]               = useState('');
  const [searchInput, setSearchInput]     = useState('');

  const [modalOpen, setModalOpen]         = useState(false);
  const [deactivateAlert, setDeactivateAlert] = useState(false);
  const [deactivateUser, setDeactivateUser]   = useState({ id: null, name: '' });
  const [details, setDetails]             = useState('');
  const [loading, setLoading]             = useState(false);
  const [openSnackbar, setOpenSnackbar]   = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [mode, setMode]                   = useState('');
  const [clientId, setClientId]           = useState(null);
  const [editData, setEditData]           = useState('');

  // ─── Fetch with server-side pagination + search ───────────────────────────
  const getClientLists = useCallback(async (currentPage, limit, searchTerm) => {
    setLoading(true);
    try {
      const response = await adminServices.getContentWriterList(
        currentPage + 1,  // API is 1-indexed, MUI is 0-indexed
        limit,
        searchTerm
      );
      if (response.ok) {
        setClientList(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getClientLists(page, rowsPerPage, search);
  }, [page, rowsPerPage, search]);

  // ─── Search with debounce ─────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);          // reset to page 1 on new search
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ─── Pagination handlers ──────────────────────────────────────────────────
  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ─── Action handlers ──────────────────────────────────────────────────────
  const handleEditButton = (data) => {
    setMode('edit');
    setClientId(data.id);
    setEditData(data);
    setModalOpen(true);
  };

  const handleViewButton = (data) => {
    navigate('/app/content-writer-detailsview', { state: data });
  };

  const handleOpenDeactivateModal = (row, message) => {
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
    setDeactivateUser({ id: row.id, name });
    setDetails(message);
    setDeactivateAlert(true);
  };

  const handleModalClose = () => setModalOpen(false);

  const snakbarHandle = (message) => {
    setOpenSnackbar(true);
    setSnackbarTitle(message);
  };

  const handleDelete = async (data) => {
    // data = 'Deactivate' | 'Reactivate'
    const status = data === 'Deactivate' ? 'INACTIVE' : 'ACTIVE';
    try {
      const response = await adminServices.deleteContentWriterList(deactivateUser.id, status);
      if (response.ok) {
        getClientLists(page, rowsPerPage, search);
        setSnackbarTitle(`${data}d Successfully`);
      } else {
        setSnackbarTitle(response.data?.error || 'Something went wrong');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDeactivateAlert(false);
      setOpenSnackbar(true);
    }
  };

  // ─── Deactivate confirm modal ─────────────────────────────────────────────
  function DeactivateModal({ open, userName, onConfirm, onCancel }) {
    if (!open) return null;
    const isDeactivate = details === 'Deactivate';
    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: 12, padding: '32px 28px 24px',
          width: 380, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}>
          <h2 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: '#111827' }}>
            {isDeactivate ? 'Deactivate Content Creator' : 'Reactivate Content Creator'}
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 14, color: '#4B5563', lineHeight: 1.6 }}>
            {isDeactivate ? (
              <><strong>{userName}</strong> will lose access to the platform.<br />Their content will not be affected.</>
            ) : (
              <><strong>{userName}</strong> will regain access to the platform.</>
            )}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button
              onClick={onCancel}
              style={{
                padding: '9px 22px', fontSize: 14, fontWeight: 500, color: '#374151',
                backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(details)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all ${
                isDeactivate
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isDeactivate ? 'Deactivate' : 'Reactivate'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      key: 'name',
      label: t('NAME'),
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <span className="font-semibold text-gray-900 text-sm">
          {`${row.first_name || ''} ${row.last_name || ''}`.trim()}
        </span>
      ),
    },
    {
      key: 'email',
      label: t('EMAIL'),
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <Tooltip title={row.email}>
          <span className="text-sm text-gray-600 truncate max-w-[200px] block">{row.email}</span>
        </Tooltip>
      ),
    },
     {
      key: 'schema_name',
      label: t('schema name'),
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <Tooltip title={row.schema_name}>
          <span className="text-sm text-gray-600 truncate max-w-[200px] block">{row.schema_name}</span>
        </Tooltip>
      ),
    },
    {
      key: 'status',
      label: t('STATUS'),
      centered: true,
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => {
        const isActive = row?.user_status === 'Active';
        return (
          <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold border ${
            isActive
              ? 'text-green-700 bg-green-50 border-green-200'
              : 'text-[#71717A] bg-[#71717A]/10 border-[#71717A]/30'
          }`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      key: 'assessments',
      label: t('ASSESSMENTS'),
      centered: true,
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <span className="text-sm text-gray-700">{row?.assessments?.length ?? 0}</span>
      ),
    },
    {
      key: 'courses',
      label: t('COURSES'),
      centered: true,
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <span className="text-sm text-gray-700">{row?.courses?.length ?? 0}</span>
      ),
    },
    {
      key: 'actions',
      label: t('ACTIONS'),
      centered: true,
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => {
        const isActive = row?.user_status === 'Active';
        const isInactive = row?.user_status === 'In Active';
        const isDeleted = row?.user_status === 'Delete';
        return (
          <div className="flex items-center justify-center gap-3">

            {/* View */}
            <button
              onClick={() => handleViewButton(row)}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span>{t('View')}</span>
            </button>

            {/* Edit */}
            <button
              onClick={() => handleEditButton(row)}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
            >
              <Pencil size={16} />
              <span>{t('Edit')}</span>
            </button>

            {isActive && (
              <button
                onClick={() => handleOpenDeactivateModal(row, 'Deactivate')}
                className="flex items-center gap-1 text-red-500 hover:text-red-700 text-sm font-medium transition-colors"
              >
                <PowerOff size={16} />
                <span>{t('Deactivate')}</span>
              </button>
            )}

            {isInactive && (
              <button
                onClick={() => handleOpenDeactivateModal(row, 'Reactivate')}
                className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm font-medium transition-colors"
              >
                <Power size={16} />
                <span>{t('Reactivate')}</span>
              </button>
            )}

            {isDeleted && (
              <button
                onClick={() => handleOpenDeactivateModal(row, 'Reactivate')}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
              >
                <Power size={16} />
                <span>{t('Reactivate')}</span>
              </button>
            )}

          </div>
        );
      },
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Page title={t('ContentWriter')}>
      <div className="flex items-start justify-between m-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('Content Creators')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('Master Organisation content creators')}</p>
        </div>
        <Button
          variant="contained"
          onClick={() => { setMode('create'); setModalOpen(true); }}
          style={{
            backgroundColor: '#2563EB', borderRadius: '8px', textTransform: 'none',
            fontWeight: 600, fontSize: '0.875rem', padding: '8px 18px', boxShadow: 'none',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
            strokeLinejoin="round" style={{ marginRight: 6 }}>
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('Add Content Creator')}
        </Button>
      </div>

      {/* ── Search bar ── */}
      <div className="mx-6 mb-4">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full max-w-sm px-4 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <ResponsiveTable
          columns={columns}
          data={clientList}
          loading={loading}
          emptyStateComponent={
            <div className="text-center py-16 text-gray-400 text-sm">
              No content creators found.
            </div>
          }
        />
      </div>

      {/* ── Server-side pagination ── */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Modal
        title={mode === 'create' ? t('Add Content Creator') : t('Edit Content Creator')}
        open={modalOpen}
        toggle={handleModalClose}
        children={
          <AddContentWriterForm
            existingClients={clientList}
            mode={mode}
            clientId={clientId}
            getClientList={() => getClientLists(page, rowsPerPage, search)}
            data={editData}
            toggle={handleModalClose}
            snackBarControl={snakbarHandle}
          />
        }
      />

      <DeactivateModal
        open={deactivateAlert}
        userName={deactivateUser.name}
        onConfirm={handleDelete}
        onCancel={() => setDeactivateAlert(false)}
      />

      <SnackBar
        open={openSnackbar}
        snackbarTitle={snackbarTitle}
        close={() => setOpenSnackbar(false)}
      />
    </Page>
  );
}